<template>
  <div class="indicator" style="display:block;">
    <slot></slot>
  </div>
</template>

<script>
import { ref, defineModel, watch, onMounted, onBeforeUnmount, inject } from 'vue'
import { Graphics, Rectangle, Text } from 'pixi.js-legacy'
import { getColor } from '../utilities/colors'

export default {
  name: 'indicator',
  props: {
    value: {
      type: Number,
      default: 100
    },
    appProp: Object,
    freeze: {
      type: Boolean,
      default: false
    },
    text: {
      type: [String, Number],
      default: ''
    },
    sense: {
      type: String,
      default: 'top',
      validator: val => ['top', 'bottom'].includes(val)
    },
    containment: Array,
    colorHex: {
      type: String,
      validator: val =>
        !val || parseInt(val, 16).toString(16) === val.toLowerCase()
    },
    color: {
      type: String,
      default: 'green',
      validator: val => ['green', 'yellow', 'orange', 'blue'].includes(val)
    }
  },
  emits: ['input', 'update:value','is-dragging', 'drag-end'],
  setup(props, { emit }) {
    // Inject from parent
    const convertToPixels = inject('convertToPixels')
    const convertToUnits = inject('convertToUnits')
    const panelHeight = inject('panelHeight')
    const addToPanel = inject('addToPanel')
    let startX = 0;
    // PIXI instances
    const instance = new Graphics()
    const textInstance = new Text(props.text, { fontSize: 15 })
    // State
    const dragging = ref(false)

    // Convert color
    const colorInt = ref(
      props.colorHex ? parseInt(props.colorHex, 16) : getColor(props.color)
    )

    // Update color if props.color or colorHex changes
    watch(
      () => [props.color, props.colorHex],
      () => {
        colorInt.value = props.colorHex
          ? parseInt(props.colorHex, 16)
          : getColor(props.color)
      }
    )

    function castToBoundaries(position) {
      if (!props.containment) return position
      const maxPos = Math.max(...props.containment)
      const minPos = Math.min(...props.containment)
      return Math.max(minPos, Math.min(maxPos, position))
    }

    function dragStart(event) {
      if (props.freeze) return
      dragging.value = true
      if (event){
        window.addEventListener('pointerup', dragEnd);
        window.addEventListener('pointermove', dragMove);
      	startX = event.client.x - event.global.x;
      }
      emit('is-dragging', true)
    }

    function dragEnd() {
      if (props.freeze) return
      if (dragging.value) emit('drag-end')
      startX = null;
      window.removeEventListener('pointerup', dragEnd);
      window.removeEventListener('pointermove', dragMove);
      dragging.value = false
      emit('is-dragging', false)
    }

    function dragMove(event) {
      if (dragging.value && !props.freeze) {
        let units = convertToUnits(event.clientX - startX)
	     units = castToBoundaries(units)
        emit('input', units)
      }
    }

    function dragTo(x) {
      dragStart()
      if (!props.freeze) {
        let units = convertToUnits(x)
        units = castToBoundaries(units)
        emit('input', units)
      }
      dragEnd()
      emit('is-dragging', true)
    }

    function render() {
      const sense = props.sense === 'top' ? -1 : 1

      instance.name = 'Indicator'
      instance.clear()
        .beginFill(colorInt.value)
        .lineStyle(2, 0x000000, 1, 1)
        .moveTo(0, 0)
        .lineTo(-5, sense * 5)
        .lineTo(-30, sense * 5)
        .lineTo(-30, sense * 40)
        .lineTo(+30, sense * 40)
        .lineTo(+30, sense * 5)
        .lineTo(+5, sense * 5)
        .closePath()
        .endFill()

      instance.x = convertToPixels(props.value)
      instance.y = 10 + (panelHeight / 30) * (props.sense === 'top' ? 9 : 11)
      instance.zIndex = 10

      textInstance.anchor.set(0.5)
      textInstance.x = 0
      textInstance.y = sense * 22
      textInstance.zIndex = 12
      textInstance.text = props.text

      instance.removeChildren()
      instance.addChild(textInstance)

      // Setup interaction
      const boxHeight = (35 - 35 * sense) / 2
      const yOfArrowEnd = 5 * sense
      const yOfTopOfBox = yOfArrowEnd - boxHeight

      instance.eventMode = 'static'
      instance.cursor = props.freeze ? null : 'grab'
      instance.hitArea = new Rectangle(-30, yOfTopOfBox, 60, 35)

      instance.on('pointerdown', dragStart)
      // instance.on('pointerup', dragEnd)
      // instance.on('pointerupoutside', dragEnd)
      // instance.on('pointermove', dragMove)
      // window.addEventListener('mousemove', dragMove);
      addToPanel(instance)
    }

    function reRender() {
      instance.x = convertToPixels(props.value)
    }

    // Watchers
    watch(() => props.value, (val) => {
      instance.x = convertToPixels(val)
    })

    watch(() => props.text, (val) => {
      textInstance.text = val
    })

    watch(() => props.freeze, (val) => {
      instance.cursor = val ? null : 'grab'
    })

    onMounted(() => {
      render()
    })

    onBeforeUnmount(() => {
      if (instance.parent) {
        instance.parent.removeChild(instance)
      }
      instance.destroy({ children: true })
    })

    return {
      // expose if needed
      reRender,
      dragTo,
      dragStart,
      dragMove,
      dragEnd,
      dragging,
      reRender,
      startX,
      render,
      castToBoundaries,
      colorInt,
      instance,
      textInstance
    }
  }
}
</script>

<style scoped>
</style>
